import React, { useCallback, useEffect, useState } from 'react';
import { Clock3, Eye, ShieldCheck, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { API_CONFIG } from '../../utils/config';
import { fetchWithAuthTimeout } from '../../utils/fetchWithAuth';
import {
  clearFounderSupportSession,
  type FounderSupportSession,
} from '../../utils/founderSupportSession';
import './FounderSupportStatusBanner.css';

interface FounderSupportStatusBannerProps {
  session: FounderSupportSession | null;
  onAdminExit: () => void;
}

type VisibleSession = Omit<FounderSupportSession, 'token'>;

const isAdminUser = (user: ReturnType<typeof useAuth>['user']) => Boolean(
  user?.isAdmin
  || user?.roles?.includes('Admin')
  || user?.roles?.includes('admin'),
);

const parseSessions = (payload: unknown): VisibleSession[] => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const sessions = (payload as { sessions?: unknown }).sessions;
  if (!Array.isArray(sessions)) return [];
  return sessions.filter((item): item is VisibleSession => (
    Boolean(item)
    && typeof item === 'object'
    && typeof (item as VisibleSession).id === 'string'
    && typeof (item as VisibleSession).expiresAt === 'string'
    && typeof (item as VisibleSession).reason === 'string'
  ));
};

export const FounderSupportStatusBanner: React.FC<FounderSupportStatusBannerProps> = ({
  session,
  onAdminExit,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [activeSessions, setActiveSessions] = useState<VisibleSession[]>([]);
  const [ending, setEnding] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const admin = isAdminUser(user);

  const formatExpiry = useCallback((value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }, [i18n.language, i18n.resolvedLanguage]);

  const fetchActiveSessions = useCallback(async () => {
    if (!user || admin || session) return;
    try {
      const response = await fetchWithAuthTimeout(
        `${API_CONFIG.BACKEND_URL}/api/support-sessions/active`,
        { cache: 'no-store' },
      );
      if (!response.ok) return;
      setActiveSessions(parseSessions(await response.json()));
    } catch {
      // Absence of a banner is safer than inventing support activity.
    }
  }, [admin, session, user]);

  useEffect(() => {
    void fetchActiveSessions();
    if (!user || admin || session) return undefined;
    const interval = window.setInterval(() => void fetchActiveSessions(), 30_000);
    return () => window.clearInterval(interval);
  }, [admin, fetchActiveSessions, session, user]);

  useEffect(() => {
    if (!session) return undefined;
    const remaining = Date.parse(session.expiresAt) - Date.now();
    if (remaining <= 0) {
      clearFounderSupportSession();
      onAdminExit();
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      clearFounderSupportSession();
      onAdminExit();
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [onAdminExit, session]);

  const endAdminSession = async () => {
    if (!session || ending) return;
    setEnding(true);
    try {
      await fetchWithAuthTimeout(
        `${API_CONFIG.BACKEND_URL}/api/admin/support-sessions/${encodeURIComponent(session.id)}`,
        { method: 'DELETE' },
      );
    } finally {
      clearFounderSupportSession();
      setEnding(false);
      onAdminExit();
    }
  };

  const revokeSession = async (id: string) => {
    if (revokingId) return;
    setRevokingId(id);
    try {
      const response = await fetchWithAuthTimeout(
        `${API_CONFIG.BACKEND_URL}/api/support-sessions/${encodeURIComponent(id)}/revoke`,
        { method: 'POST' },
      );
      if (response.ok) {
        setActiveSessions((current) => current.filter((item) => item.id !== id));
      }
    } finally {
      setRevokingId(null);
    }
  };

  if (session) {
    return (
      <aside className="founder-support-banner founder-support-banner--admin" role="status">
        <div className="founder-support-banner__icon" aria-hidden="true">
          <Eye size={18} />
        </div>
        <div className="founder-support-banner__copy">
          <strong>{t('founderSupport.banner.adminTitle')}</strong>
          <span>
            {t('founderSupport.banner.adminBody', { email: session.targetEmail })}
          </span>
          <small>
            <Clock3 size={12} aria-hidden="true" />
            {t('founderSupport.banner.expiresAt', { time: formatExpiry(session.expiresAt) })}
            <span aria-hidden="true"> / </span>
            {session.reason}
          </small>
        </div>
        <button type="button" onClick={() => void endAdminSession()} disabled={ending}>
          <X size={15} aria-hidden="true" />
          {ending ? t('founderSupport.banner.ending') : t('founderSupport.banner.exit')}
        </button>
      </aside>
    );
  }

  if (activeSessions.length === 0) return null;
  const active = activeSessions[0];
  return (
    <aside className="founder-support-banner founder-support-banner--founder" role="status">
      <div className="founder-support-banner__icon" aria-hidden="true">
        <ShieldCheck size={18} />
      </div>
      <div className="founder-support-banner__copy">
        <strong>{t('founderSupport.banner.founderTitle')}</strong>
        <span>{t('founderSupport.banner.founderBody')}</span>
        <small>
          <Clock3 size={12} aria-hidden="true" />
          {t('founderSupport.banner.expiresAt', { time: formatExpiry(active.expiresAt) })}
        </small>
      </div>
      <button
        type="button"
        onClick={() => void revokeSession(active.id)}
        disabled={revokingId === active.id}
      >
        {revokingId === active.id
          ? t('founderSupport.banner.revoking')
          : t('founderSupport.banner.revoke')}
      </button>
    </aside>
  );
};

export default FounderSupportStatusBanner;
